/**
 * Margin Indicator Component
 *
 * Displays the theoretical margin with color coding based on level.
 */

import { TrendingUp, TrendingDown } from 'lucide-react';
import { formatCentimes } from '@/lib/format';

interface MarginIndicatorProps {
  /** Selling price in cents */
  sellingPrice: number | null;
  /** Theoretical cost in cents */
  theoreticalCost: number | null;
  /** Currency code */
  currency?: string;
  /** Whether to show the absolute amount */
  showAmount?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

interface MarginInfo {
  amount: number;
  percent: number;
  level: 'good' | 'warning' | 'danger';
}

function calculateMargin(sellingPrice: number, theoreticalCost: number): MarginInfo {
  const marginAmount = sellingPrice - theoreticalCost;
  const marginPercent = (marginAmount / sellingPrice) * 100;

  let level: 'good' | 'warning' | 'danger';
  if (marginPercent >= 60) {
    level = 'good';
  } else if (marginPercent >= 40) {
    level = 'warning';
  } else {
    level = 'danger';
  }

  return {
    amount: marginAmount,
    percent: marginPercent,
    level,
  };
}

const levelColors: Record<'good' | 'warning' | 'danger', string> = {
  good: 'text-success-600',
  warning: 'text-warning-600',
  danger: 'text-danger-600',
};

const levelBgColors: Record<'good' | 'warning' | 'danger', string> = {
  good: 'bg-success-50',
  warning: 'bg-warning-50',
  danger: 'bg-danger-50',
};

export function MarginIndicator({
  sellingPrice,
  theoreticalCost,
  currency = 'EUR',
  showAmount = false,
  size = 'md',
}: MarginIndicatorProps) {
  // Can't calculate margin if missing data
  if (sellingPrice === null || theoreticalCost === null || sellingPrice === 0) {
    return <span className="text-sm text-neutral-400">-</span>;
  }

  const margin = calculateMargin(sellingPrice, theoreticalCost);
  const isPositive = margin.amount >= 0;

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-lg',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Format values
  const formatPercent = (percent: number) => `${percent.toFixed(1)}%`;
  const formatAmount = (cents: number) => formatCentimes(Math.abs(cents), currency);

  return (
    <div className={`flex items-center gap-1 ${levelColors[margin.level]}`}>
      {isPositive ? (
        <TrendingUp className={iconSizes[size]} />
      ) : (
        <TrendingDown className={iconSizes[size]} />
      )}
      <span className={`${textSizes[size]} font-medium`}>
        {formatPercent(margin.percent)}
      </span>
      {showAmount && (
        <span className={`${textSizes[size]} text-neutral-500 ml-1`}>
          ({isPositive ? '+' : '-'}{formatAmount(margin.amount)})
        </span>
      )}
    </div>
  );
}

/**
 * Large margin display card for drawer/detail views
 */
export function MarginCard({
  sellingPrice,
  theoreticalCost,
  currency = 'EUR',
}: {
  sellingPrice: number | null;
  theoreticalCost: number | null;
  currency?: string;
}) {
  // Can't calculate margin if missing data
  if (sellingPrice === null || theoreticalCost === null || sellingPrice === 0) {
    return (
      <div className="rounded-lg bg-neutral-50 p-4">
        <div className="flex items-center gap-2 text-neutral-500">
          <TrendingUp className="w-5 h-5" />
          <div>
            <p className="text-xs uppercase tracking-wide">Marge</p>
            <p className="font-semibold">Non disponible</p>
          </div>
        </div>
      </div>
    );
  }

  const margin = calculateMargin(sellingPrice, theoreticalCost);

  return (
    <div className={`rounded-lg p-4 ${levelBgColors[margin.level]}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-white ${levelColors[margin.level]}`}>
          {margin.amount >= 0 ? (
            <TrendingUp className="w-5 h-5" />
          ) : (
            <TrendingDown className="w-5 h-5" />
          )}
        </div>
        <div>
          <p className="text-xs text-neutral-600 uppercase tracking-wide">Marge theorique</p>
          <p className={`text-2xl font-bold ${levelColors[margin.level]}`}>
            {margin.percent.toFixed(1)}%
          </p>
          <p className="text-sm text-neutral-600">
            {formatCentimes(margin.amount, currency)} brut
          </p>
        </div>
      </div>
    </div>
  );
}
